import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Observable } from 'rxjs';

import {
  ActivateDeactivateCommands,
  DeviceConnectionStatus,
  DeviceFirmwareVersion,
  DeviceIds,
  DeviceNameCommands,
  DeviceNameProperties,
  Feature,
  FeatureCommands,
  FeatureNames,
  FeaturePropertyKey,
  FhtValveCommands,
  FhtValveProperties,
  IsActiveProperty,
  IsNumberValueProperty,
} from '@vi-iot/client';

import { NO_VALUE } from '@pipes';
import { GTMService } from '@services';
import {
  ActiveErrorData,
  ActiveErrorsStore,
  GatewayStore,
  getFhtMainFromChannel,
  InstallationFeaturesStore,
} from '@store';

import { InstallationService } from '../../services/installation.service';
import { EditDeviceNameModalComponent } from '../edit-device-name-modal/edit-device-name-modal.component';
import { FhtManagerModalComponent } from '../managers/fht-manager-modal/fht-manager-modal.component';

@Component({
  selector: 'app-fht-channel-panel',
  templateUrl: './fht-channel-panel.component.html',
  styleUrls: ['./fht-channel-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FhtChannelPanelComponent implements OnInit {
  readonly featurePropertyKey = FeaturePropertyKey;
  readonly FeatureCommands = FeatureCommands;
  readonly noValue = NO_VALUE;
  valvePosition$: Observable<Feature<IsNumberValueProperty, never>>;
  valveOpenedOrClosed$: Observable<Feature<FhtValveProperties, FhtValveCommands>>;
  isSrcStandalone$: Observable<Feature<IsActiveProperty, never>>;
  deviceErrors$: Observable<ActiveErrorData[]>;
  burnInMode$: Observable<Feature<IsActiveProperty, ActivateDeactivateCommands>>;
  deviceName$: Observable<Feature<DeviceNameProperties, DeviceNameCommands>>;
  firmware$: Observable<Feature<DeviceFirmwareVersion, never>>;
  editDeviceNameDialogRef: MatDialogRef<EditDeviceNameModalComponent>;
  featuresLoading$: Observable<boolean>;

  @Input() deviceId: string;

  constructor(
    public installationService: InstallationService,
    public gatewayStore: GatewayStore,
    public activeErrorsStore: ActiveErrorsStore,
    private dialog: MatDialog,
    private gtmService: GTMService,
    private installationFeaturesStore: InstallationFeaturesStore
  ) {}

  ngOnInit(): void {
    this.featuresLoading$ = this.installationFeaturesStore.featuresLoading$;
    this.isSrcStandalone$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.standaloneMode,
      DeviceIds.RoomControl
    );
    this.valvePosition$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.fhtChannelValvePosition,
      this.deviceId
    );
    this.valveOpenedOrClosed$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.fhtChannelValve,
      this.deviceId
    );
    this.deviceErrors$ = this.activeErrorsStore.getActiveErrorsForSRCDevice(this.deviceId);
    this.burnInMode$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.fhtChannelBurnInMode,
      this.deviceId
    );
    this.deviceName$ = this.installationFeaturesStore.selectInstallationDeviceFeature<
      DeviceNameProperties,
      DeviceNameCommands
    >(FeatureNames.deviceName, this.deviceId);
    this.firmware$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.essFirmwareBundle,
      this.deviceId
    );
  }

  openEditDeviceNameDialog(deviceId: string): void {
    this.gtmService.track('onOpen', 'Edit Device Name modal', 'singleRoomControl');
    this.editDeviceNameDialogRef = this.dialog.open(EditDeviceNameModalComponent, {
      width: '715px',
      panelClass: 'commissioning-edit-room-dialog',
      data: {
        deviceId,
      },
    });
  }
  setValve(): void {
    const fhtMainId = getFhtMainFromChannel(this.deviceId);
    this.installationService.selectedFhtDeviceId$.next(fhtMainId);
  }

  getFHTConnectionStatusFeature(): Observable<Feature<DeviceConnectionStatus, never>> {
    const fhtMainId = getFhtMainFromChannel(this.deviceId);

    return this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.deviceConnection,
      fhtMainId
    );
  }

  openFhtModal(id: string): void {
    this.gtmService.track('onOpen', 'FHT modal', 'singleRoomControl');
    this.installationService.selectedFhtDeviceId$.next(id);

    this.dialog.open(FhtManagerModalComponent, {
      width: '880px',
      panelClass: 'app-fht-manager-dialog',
      disableClose: false,
    });
  }

  onBurnInModeChange(event: MouseEvent, value: boolean): void {
    event.preventDefault();
    event.stopImmediatePropagation();
    this.installationFeaturesStore.updateFeature(
      FeatureNames.fhtChannelBurnInMode,
      value,
      this.featurePropertyKey.active,
      null,
      value ? FeatureCommands.activate : FeatureCommands.deactivate,
      null,
      this.deviceId
    );
  }
}
